import { SendIcon, UsersIcon } from 'lucide-react'
import React from 'react'

import { Banner, TextButton, Tooltip, TooltipProvider } from 'slab'

const ENROLLMENT_STATUSES = [
  'invited_combined', // represents `invite_pending`, `invited`, and `pending_automated_invite`
  'in_progress',
  'enrolled',
  'declined',
  'expired'
]

const STATUS_LABELS: Record<string, string> = {
  invited_combined: 'Not started',
  in_progress: 'Viewing plans',
  enrolled: 'Enrolled',
  declined: 'Declined',
  expired: 'Invite expired'
}

const STATUS_TOOLTIPS: Record<string, string> = {
  invited_combined: 'These employees have received or are scheduled to receive an invitation, but have not set up their Thatch account yet',
  in_progress: 'These employees have set up their Thatch account and are viewing their health benefit options',
  declined: 'These employees have declined their health benefits',
  enrolled: 'These employees have enrolled in health benefits',
  expired: "These employees didn't decline or enroll in coverage during their enrollment window"
}

type EnrollmentRateTrackerProps = {
  enrollmentRate: number,
  statusCounts: Record<string, number>,
  onStatusClick: (status: string) => void,
  activeStatus: string | null,
  showInviteBanner?: boolean,
  onSelectAllEmployees?: () => void,
}

const EnrollmentRateTracker: React.FC<EnrollmentRateTrackerProps> = ({ enrollmentRate, statusCounts, onStatusClick, activeStatus, showInviteBanner, onSelectAllEmployees }) => {
  const combinedCounts: Record<string, number> = {
    in_progress: statusCounts['in_progress'] || 0,
    enrolled: statusCounts['enrolled'] || 0,
    declined: statusCounts['declined'] || 0,
    expired: statusCounts['expired'] || 0,
    // Combine all 'invited' type statuses into one category for the tracker
    invited_combined:
      (statusCounts['invite_pending'] || 0) +
      (statusCounts['invited'] || 0) +
      (statusCounts['pending_automated_invite'] || 0),
  }

  const total = ENROLLMENT_STATUSES.reduce((sum, s) => sum + (combinedCounts[s] || 0), 0)

  const handleStatusClick = (status: string) => {
    window.segment?.track('[ER] Enrollment status clicked', {
      status,
      status_label: STATUS_LABELS[status],
      enrollment_rate: Math.round(enrollmentRate * 100),
      total_employees: total,
      status_count: combinedCounts[status] || 0,
    })

    onStatusClick(status)
  }

  return (
    <div className='bg-white rounded-2xl border border-gray-200 p-6 shadow-sm mb-6'>
      <div className='flex flex-col xl:flex-row gap-4 items-start xl:items-center justify-between'>
        <div className='w-full xl:w-2/5 xl:pr-4 flex flex-col'>
          <div className='body text-gray-500'>Enrollment rate</div>
          <div className='flex items-end justify-between gap-4 my-1'>
            <div className='heading-lg font-medium text-gray-800'>{Math.round(enrollmentRate * 100)}%</div>
            <div className='body text-gray-500 flex items-center gap-1 mb-1'>
              <UsersIcon className='w-4 h-4' />
              {combinedCounts['enrolled'] || 0}/{total}
            </div>
          </div>
          <div className='w-full h-2 rounded-full bg-gray-200 flex overflow-hidden'>
            <div
              className='bg-green-300 rounded-full transition-all duration-300 h-2'
              style={{ width: total > 0 ? `${((combinedCounts['enrolled'] || 0) / total) * 100}%` : '0%' }}
            />
          </div>
        </div>

        <div className='w-full xl:w-3/5 grid grid-cols-2 sm:grid-cols-3 lg:flex gap-3 xl:gap-4'>
          <TooltipProvider>
            {ENROLLMENT_STATUSES.map((status) => {
              const isActive = activeStatus === status
              return (
                <Tooltip key={status} content={STATUS_TOOLTIPS[status]} side='top' zIndex={1000}>
                  <button
                    className={`inline-flex flex-col text-left py-3 px-4 rounded-2xl border transition-all duration-200 items-start gap-1 \
                      ${isActive
                        ? 'border-purple-400 bg-purple-50'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-[0_0_16px_rgba(147,51,234,0.3)]'}`}
                    onClick={() => handleStatusClick(status)}
                    type='button'
                    style={{
                      flex: 1,
                      minWidth: 0,
                    }}
                  >

                    <div className='inline-flex items-center gap-2 align-middle h-full'>
                      <div className='body-sm text-gray-500 self-center'>
                        {STATUS_LABELS[status]}
                      </div>
                    </div>

                    <div className='heading-md text-gray-800 text-left'>{combinedCounts[status] || 0}</div>
                  </button>
                </Tooltip>
              )
            })}
          </TooltipProvider>
        </div>
      </div>
      {showInviteBanner && (
        <div className='mt-6'>
          <Banner
            type='info'
            title='Ready to invite your team?'
            icon={SendIcon}

          >
            You have{' '}
            <TextButton
              onClick={onSelectAllEmployees}
              color='inherit'
            >
              employees that are ready to be invited
            </TextButton>
            . Select the employees to invite and click "Invite selected" to get started.
          </Banner>
        </div>
      )}
    </div>
  )
}

export default EnrollmentRateTracker
